<template>
  <article
    class="flyer-card overflow-hidden border border-slate-200 bg-white shadow-sm transition focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600"
    :class="[
      view === 'post' ? 'rounded-xl' : 'mb-3 break-inside-avoid rounded-xl',
      {
        'flyer-card-review': isBusinessPost,
        'group hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-md': isCardInteractive,
        'cursor-pointer': canOpenBusinessPost
      }
    ]"
    :role="canOpenBusinessPost ? 'link' : undefined"
    :tabindex="canOpenBusinessPost ? 0 : undefined"
    :aria-label="canOpenBusinessPost ? `Open ${isCheckin ? 'checkin' : 'review'} for ${businessName}` : undefined"
    @click="openReviewFlyer"
    @keydown="handleCardKeydown"
  >
    <div class="relative">
      <div
        v-if="!reviewContentOnly && !isCheckin"
        class="absolute right-2 z-10"
        :class="isBusinessPost ? 'top-[1.625rem] sm:top-[1.875rem]' : 'top-2'"
      >
        <button
          class="grid h-6 w-6 place-items-center rounded-full bg-white/90 text-slate-700 shadow-sm ring-1 ring-slate-200 backdrop-blur transition hover:bg-white hover:text-slate-950"
          type="button"
          aria-label="Flyer actions"
          @click.stop="menuOpen = !menuOpen"
        >
          <svg class="h-4 w-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <circle cx="12" cy="8" r="2.2" />
            <circle cx="12" cy="16" r="2.2" />
          </svg>
        </button>

        <div v-if="menuOpen" class="absolute right-0 mt-2 grid min-w-[8.5rem] overflow-hidden rounded-xl border border-slate-200 bg-white py-1 shadow-xl shadow-slate-950/10">
          <button v-if="!isOwnFlyer" class="flyer-menu-item" type="button" @click.stop="toggleLike">
            <i class="text-base" :class="liked ? 'ri-heart-fill text-rose-500' : 'ri-heart-line'" aria-hidden="true"></i>
            <span>{{ liked ? 'Liked' : 'Like' }}</span>
          </button>
          <button v-if="!isOwnFlyer" class="flyer-menu-item" type="button" @click.stop="openSaveBoard">
            <i class="text-base" :class="saved ? 'ri-bookmark-fill text-blue-600' : 'ri-bookmark-line'" aria-hidden="true"></i>
            <span>{{ saved ? 'Saved' : 'Save' }}</span>
          </button>
          <button class="flyer-menu-item" type="button" @click.stop="openShare">
            <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <circle cx="18" cy="5" r="3" />
              <circle cx="6" cy="12" r="3" />
              <circle cx="18" cy="19" r="3" />
              <path d="m8.6 10.7 6.8-4.4M8.6 13.3l6.8 4.4" />
            </svg>
            <span>Share</span>
          </button>
          <button class="flyer-menu-item text-blue-700 hover:bg-blue-50" type="button" @click.stop="openReport">
            <svg class="h-4 w-4 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M4 21V5a2 2 0 0 1 2-2h9l.5 3H20v10h-5l-.5-3H6" />
            </svg>
            <span>Report</span>
          </button>
        </div>
      </div>

      <router-link v-if="!isBusinessPost" :to="`/flyers/${flyer.id}`" class="block">
        <div
          v-if="flyer.image"
          class="flyer-card-media bg-slate-100"
          :class="view === 'post' ? 'flyer-card-media-post' : 'flyer-card-media-masonry'"
        >
          <img
            class="h-auto w-full"
            :src="flyer.image"
            :alt="flyer.title || categoryLabel || 'Flyer image'"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
          />
        </div>
        <div
          v-else
          class="flyer-card-empty flex items-center justify-center text-white"
          :class="view === 'post' ? 'flyer-card-empty-post' : 'flyer-card-empty-masonry'"
          :style="{ background: fallbackBackground }"
        >
          <div class="min-w-0 text-center">
            <p class="m-0 line-clamp-5 text-sm font-normal leading-6 text-white sm:text-base">
              {{ textPostBody }}
            </p>
          </div>
        </div>
      </router-link>
    </div>

    <div class="grid gap-1.5 p-2.5 sm:gap-2 sm:p-3.5">
      <div v-if="isBusinessPost" class="grid gap-2.5">
        <div
          v-if="!reviewContentOnly"
          class="flex min-w-0 items-center gap-2.5 rounded-xl bg-slate-50 p-2.5 transition group-hover:bg-slate-100"
          :class="{ 'mr-8': !isCheckin }"
        >
          <span class="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-blue-600 text-sm font-bold text-white">
            {{ businessInitial }}
          </span>
          <span class="min-w-0 flex-1">
            <span class="block truncate text-sm font-semibold text-slate-950">{{ businessName }}</span>
            <span class="mt-0.5 flex min-w-0 items-center gap-1 text-[0.7rem] text-slate-500">
              <i class="ri-map-pin-2-line shrink-0 text-blue-600" aria-hidden="true"></i>
              <span class="truncate">{{ businessAddress || 'Address not available' }}</span>
            </span>
          </span>
        </div>

        <div v-if="isReview" class="flex items-center gap-2.5">
          <span
            v-if="reviewContentOnly"
            class="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-xl"
            :class="reviewTone.badgeClass"
            :aria-label="reviewTone.label"
            role="img"
          >
            <span class="review-rating-emoji" aria-hidden="true">{{ reviewTone.emoji }}</span>
          </span>

          <div class="flex items-center gap-1" :aria-label="`${reviewRating} out of 5 stars`">
            <svg
              v-for="star in 5"
              :key="star"
              class="h-4 w-4"
              :class="star <= reviewRating ? reviewTone.starClass : 'fill-slate-100 text-slate-300'"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="1.5"
              aria-hidden="true"
            >
              <path d="m12 2.8 2.85 5.78 6.38.93-4.62 4.5 1.09 6.35L12 17.37l-5.7 3 1.09-6.35-4.62-4.5 6.38-.93L12 2.8Z" />
            </svg>
            <span class="ml-1 text-xs font-semibold text-slate-500">{{ reviewRating }}/5</span>
          </div>
        </div>

        <p
          class="m-0 text-sm font-normal leading-5 text-slate-700"
          :class="{ 'line-clamp-5': !collapsibleComment || !commentExpanded }"
        >
          {{ displayedReviewComment }}
        </p>
        <button
          v-if="isReviewCommentTruncated"
          class="w-fit text-xs font-semibold text-blue-600 hover:text-blue-800 hover:underline"
          type="button"
          :aria-expanded="commentExpanded"
          @click.stop="commentExpanded = !commentExpanded"
        >
          {{ commentExpanded ? 'Read less' : 'Read more' }}
        </button>

        <Swiper
          v-if="isReview && reviewImages.length"
          class="review-image-strip"
          :modules="reviewSwiperModules"
          :slides-per-view="3"
          :slides-per-group="1"
          :space-between="8"
          :watch-overflow="true"
          :pagination="{ clickable: true, dynamicBullets: true }"
          aria-label="Review photos"
        >
          <SwiperSlide
            v-for="(image, imageIndex) in reviewImages"
            :key="`${image}-${imageIndex}`"
          >
            <div
              class="review-image-tile"
            >
              <img
                :src="image"
                :alt="`${businessName} review photo ${imageIndex + 1}`"
                loading="lazy"
                decoding="async"
              />
            </div>
          </SwiperSlide>
        </Swiper>
      </div>

      <div v-if="!reviewContentOnly" class="flex min-w-0 items-center gap-1.5 text-[0.66rem] text-slate-500 sm:gap-3 sm:text-[0.78rem]">
        <div
          v-if="!isBusinessPost"
          class="flex min-w-0 flex-1 items-center gap-1 sm:gap-1.5"
        >
          <svg class="h-3 w-3 shrink-0 text-blue-600 sm:h-4 sm:w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
            <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 1 1 16 0Z" />
            <circle cx="12" cy="10" r="3" />
          </svg>
          <span class="truncate">{{ locationLabel }}</span>
        </div>

        <span v-if="relativeDateLabel" class="flex shrink-0 items-center gap-1" :class="{ 'ml-auto': isBusinessPost }">
          <i class="ri-time-line text-xs text-slate-400 sm:text-base" aria-hidden="true"></i>
          <span>{{ relativeDateLabel }}</span>
        </span>
      </div>

      <div v-if="showAuthor" class="flex min-w-0 items-center gap-1.5 border-t border-slate-100 pt-1.5 sm:gap-2 sm:pt-2">
        <router-link class="flex min-w-0 flex-1 items-center gap-1.5 hover:text-blue-700 sm:gap-2" :to="authorRoute" @click.stop>
          <img
            class="h-5 w-5 shrink-0 rounded-full object-cover ring-1 ring-slate-200 sm:h-7 sm:w-7"
            :src="authorAvatar"
            :alt="flyer.author || 'NetFlyr'"
            loading="lazy"
            decoding="async"
            fetchpriority="low"
          />
          <span class="truncate text-[0.68rem] font-normal text-slate-500 sm:text-[0.82rem]">
            {{ flyer.author || 'NetFlyr' }}
          </span>
        </router-link>
      </div>
    </div>

    <Teleport to="body">
      <ShareFlyerModal v-if="shareModalOpen" :flyer="flyer" @close="shareModalOpen = false" />
      <ReportFlyerModal v-if="reportModalOpen" :flyer="flyer" @close="reportModalOpen = false" @submit="handleReport" />
      <SaveToBoardModal
        v-if="saveBoardModalOpen"
        :flyer-id="flyer.id"
        :initial-saved="saved"
        :initial-board-id="savedBoardId"
        @close="saveBoardModalOpen = false"
        @saved-change="handleSavedChange"
      />
    </Teleport>
  </article>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/vue'
import ShareFlyerModal from '@/components/common/ShareFlyerModal.vue'
import ReportFlyerModal from '@/components/common/ReportFlyerModal.vue'
import SaveToBoardModal from '@/components/common/SaveToBoardModal.vue'
import api from '@/services/api'
import { useSessionStore } from '@/stores/session'
import { getFlyerBoardId, normalizeFlyerSaved } from '@/utils/flyerDetail'
import 'swiper/css'
import 'swiper/css/pagination'

const props = defineProps({
  flyer: { type: Object, required: true },
  view: { type: String, default: 'masonry' },
  showAuthor: { type: Boolean, default: true },
  reviewContentOnly: { type: Boolean, default: false },
  businessPostClickable: { type: Boolean, default: true },
  collapsibleComment: { type: Boolean, default: false }
})

const menuOpen = ref(false)
const shareModalOpen = ref(false)
const reportModalOpen = ref(false)
const saveBoardModalOpen = ref(false)
const commentExpanded = ref(false)
const reviewSwiperModules = [Pagination]
const liked = ref(Boolean(props.flyer.liked))
const saved = ref(normalizeFlyerSaved(props.flyer))
const savedBoardId = ref(String(getFlyerBoardId(props.flyer) || '').trim())
const router = useRouter()
const session = useSessionStore()

const currentUserId = computed(() => String(session.userId || session.user?.user_id || session.user?.id || '').trim())
const flyerOwnerId = computed(() =>
  String(
    props.flyer.userId ||
    props.flyer.user_id ||
    props.flyer.userid ||
    props.flyer.uid ||
    props.flyer.created_by ||
    props.flyer.created_by_id ||
    props.flyer.owner_id ||
    props.flyer.author_id ||
    ''
  ).trim()
)
const isOwnFlyer = computed(() => currentUserId.value && flyerOwnerId.value && currentUserId.value === flyerOwnerId.value)
const flyerType = computed(() => String(props.flyer.flyer_type || props.flyer.flyerType || '').toLowerCase())
const isCheckin = computed(() => flyerType.value === 'checkin')
const isReview = computed(() => (
  flyerType.value === 'review' ||
  (!flyerType.value && Boolean(props.flyer.business_name || props.flyer.businessName || props.flyer.s_business || props.flyer.review_comment || props.flyer.review))
))
const isBusinessPost = computed(() => isReview.value || isCheckin.value)
const canOpenBusinessPost = computed(() => props.businessPostClickable && ((isReview.value && Boolean(props.flyer.id)) || isCheckin.value))
const isCardInteractive = computed(() => !isBusinessPost.value || canOpenBusinessPost.value)
const businessName = computed(() => String(props.flyer.business_name || props.flyer.businessName || props.flyer.s_business || props.flyer.title || 'Business').trim())
const businessAddress = computed(() => String(props.flyer.business_adress || props.flyer.business_address || props.flyer.businessAddress || props.flyer.s_address || props.flyer.location || props.flyer.address || '').trim())
const businessInitial = computed(() => businessName.value.charAt(0).toUpperCase() || 'B')
const reviewRating = computed(() => Math.min(5, Math.max(0, Number(props.flyer.review_count || props.flyer.rating || 0))))
const reviewTone = computed(() => {
  const score = Math.round(reviewRating.value)
  const tones = {
    1: { emoji: '😠', badgeClass: 'bg-red-100 text-red-600', starClass: 'fill-red-500 text-red-500', label: 'Very negative review' },
    2: { emoji: '😞', badgeClass: 'bg-orange-100 text-orange-600', starClass: 'fill-orange-500 text-orange-500', label: 'Negative review' },
    3: { emoji: '😐', badgeClass: 'bg-amber-100 text-amber-600', starClass: 'fill-amber-400 text-amber-400', label: 'Neutral review' },
    4: { emoji: '😊', badgeClass: 'bg-lime-100 text-lime-700', starClass: 'fill-lime-500 text-lime-500', label: 'Positive review' },
    5: { emoji: '🤩', badgeClass: 'bg-emerald-100 text-emerald-600', starClass: 'fill-emerald-500 text-emerald-500', label: 'Excellent review' }
  }

  return tones[score] || {
    emoji: '💬',
    badgeClass: 'bg-slate-100 text-slate-500',
    starClass: 'fill-slate-400 text-slate-400',
    label: 'Unrated review'
  }
})
const reviewImages = computed(() => Array.from(new Set([
  ...(Array.isArray(props.flyer.images) ? props.flyer.images : []),
  props.flyer.image,
  props.flyer.image1,
  props.flyer.image2,
  props.flyer.image3,
  props.flyer.image4
].filter(Boolean))))
const authorRoute = computed(() => {
  if (isOwnFlyer.value) return '/account'
  return `/users/${props.flyer.userId || props.flyer.user_id || props.flyer.userid || props.flyer.uid || props.flyer.id}`
})
const authorAvatar = computed(() => props.flyer.authorAvatar || `https://dummyimage.com/80x80/e0e7ff/1d4ed8&text=${encodeURIComponent((props.flyer.author || 'N').charAt(0))}`)
const categoryLabel = computed(() => {
  const category = String(props.flyer.category || '').trim()
  const namedCategory =
    props.flyer.category_name ||
    props.flyer.catergory_name ||
    props.flyer.cat_name ||
    props.flyer.content_type_name ||
    props.flyer.flyer_category ||
    props.flyer.categoryTitle ||
    props.flyer.category_title ||
    ''

  if (String(namedCategory || '').trim()) return namedCategory
  return category && Number.isNaN(Number(category)) ? category : 'Flyer'
})
const locationLabel = computed(() => {
  const parts = [
    props.flyer.city,
    props.flyer.location,
    props.flyer.area,
    props.flyer.locality,
    props.flyer.state,
    props.flyer.address,
    props.flyer.street,
    props.flyer.country_id,
    props.flyer.country
  ]
    .map((value) => String(value || '').trim())
    .filter(Boolean)

  return parts[0] || 'Location not available'
})
const decodeHtmlEntities = (value = '') => {
  let decoded = String(value || '')
  if (!decoded) return ''

  for (let index = 0; index < 5; index += 1) {
    const previous = decoded
    if (typeof document !== 'undefined') {
      const textarea = document.createElement('textarea')
      textarea.innerHTML = decoded
      decoded = textarea.value
    } else {
      decoded = decoded
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/&quot;/g, '"')
        .replace(/&#039;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
    }
    if (decoded === previous) break
  }

  return decoded
}

const stripHtml = (value = '') =>
  decodeHtmlEntities(value)
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()

const reviewComment = computed(() => stripHtml(props.flyer.review_comment || props.flyer.review || props.flyer.description || ''))
const reviewCommentFallback = computed(() => isCheckin.value ? 'No checkin comment provided.' : 'No review comment provided.')
const fullReviewComment = computed(() => reviewComment.value || reviewCommentFallback.value)
const commentCharacterLimit = 180
const isReviewCommentTruncated = computed(() => (
  props.collapsibleComment && fullReviewComment.value.length > commentCharacterLimit
))
const displayedReviewComment = computed(() => {
  if (!isReviewCommentTruncated.value || commentExpanded.value) return fullReviewComment.value
  return `${fullReviewComment.value.slice(0, commentCharacterLimit).trimEnd()}…`
})
const textPostBody = computed(() => stripHtml(props.flyer.description) || props.flyer.title || categoryLabel.value || 'Flyer')
const relativeDateLabel = computed(() => {
  return props.flyer.time_ago ||
    props.flyer.time_Ago ||
    props.flyer.timeAgo ||
    props.flyer.created_at ||
    props.flyer.postedAt ||
    props.flyer.post_date ||
    props.flyer.updated_at ||
    props.flyer.date ||
    ''
})
const normalizeHex = (value) => {
  const raw = String(value || '').replace('#', '').trim()
  if (/^[0-9a-f]{3}$/i.test(raw)) {
    return raw.split('').map((char) => `${char}${char}`).join('')
  }
  return /^[0-9a-f]{6}$/i.test(raw) ? raw : '2563eb'
}
const flyerCodeColor = computed(() =>
  props.flyer.param_code ||
  props.flyer.paramCode ||
  props.flyer.code ||
  props.flyer.color_code ||
  props.flyer.colorCode ||
  props.flyer.categoryColor ||
  props.flyer.accent ||
  '#2563eb'
)
const fallbackBackground = computed(() => `#${normalizeHex(flyerCodeColor.value)}`)

const businessPostDetailRoute = computed(() => {
  if (!isCheckin.value) return `/flyers/${props.flyer.id}`

  return {
    path: `/flyers/${props.flyer.id}`,
    query: { type: 'checkin' },
    state: {
      checkin: JSON.parse(JSON.stringify(props.flyer))
    }
  }
})

const openReviewFlyer = (event) => {
  if (!canOpenBusinessPost.value) return
  const interactiveTarget = event?.target?.closest?.('a, button, input, select, textarea, [role="button"]')
  if (interactiveTarget && interactiveTarget !== event.currentTarget) return
  router.push(businessPostDetailRoute.value)
}

const handleCardKeydown = (event) => {
  if (!canOpenBusinessPost.value || event.target !== event.currentTarget) return
  if (event.key !== 'Enter' && event.key !== ' ') return
  event.preventDefault()
  router.push(businessPostDetailRoute.value)
}

const openShare = () => {
  menuOpen.value = false
  shareModalOpen.value = true
}

const openReport = () => {
  menuOpen.value = false
  reportModalOpen.value = true
}

const handleReport = (payload) => {
  console.info('Flyer report submitted', { flyerId: props.flyer.id, ...payload })
}

const requireLogin = () => {
  if (session.isLoggedIn && (session.userId || session.user?.user_id)) return true
  router.push('/login')
  return false
}

const openSaveBoard = () => {
  menuOpen.value = false
  if (isOwnFlyer.value) return
  if (!requireLogin()) return
  saveBoardModalOpen.value = true
}

const handleSavedChange = ({ saved: nextSaved, boardIds = [] } = {}) => {
  saved.value = Boolean(nextSaved)
  savedBoardId.value = saved.value
    ? boardIds.map((boardId) => String(boardId || '').trim()).find(Boolean) || savedBoardId.value
    : ''
}

watch(
  () => props.flyer,
  (nextFlyer) => {
    saved.value = normalizeFlyerSaved(nextFlyer)
    savedBoardId.value = String(getFlyerBoardId(nextFlyer) || '').trim()
    commentExpanded.value = false
  },
  { deep: true }
)

const toggleLike = async () => {
  menuOpen.value = false
  if (isOwnFlyer.value) return
  if (!requireLogin()) return

  const previous = liked.value
  liked.value = !liked.value

  try {
    await api.likeFlyer({
      flyer_id: props.flyer.id,
      user_id: session.userId || session.user?.user_id || ''
    })
  } catch {
    liked.value = previous
  }
}
</script>

<style scoped>
.flyer-card {
  transform: translateZ(0);
}

.flyer-card-review {
  border-color: rgb(226 232 240);
}

.review-rating-emoji {
  display: inline-block;
  line-height: 1;
  transform-origin: 50% 80%;
  animation: review-emoji-float 2.2s ease-in-out infinite;
}

@keyframes review-emoji-float {
  0%,
  100% {
    transform: translateY(0) rotate(-3deg) scale(1);
  }

  50% {
    transform: translateY(-3px) rotate(3deg) scale(1.08);
  }
}

.review-image-strip {
  width: 100%;
  max-width: 100%;
  padding: 0.1rem 0 1.25rem;
}

.review-image-strip :deep(.swiper-slide) {
  height: auto;
}

.review-image-strip :deep(.swiper-pagination) {
  bottom: 0.05rem;
}

.review-image-strip :deep(.swiper-pagination-bullet) {
  width: 0.38rem;
  height: 0.38rem;
  background: rgb(148 163 184);
  opacity: 0.65;
}

.review-image-strip :deep(.swiper-pagination-bullet-active) {
  background: rgb(37 99 235);
  opacity: 1;
}

.review-image-tile {
  display: grid;
  width: 100%;
  height: 100%;
  min-height: 3.5rem;
  aspect-ratio: 4 / 3;
  place-items: center;
  overflow: hidden;
  border: 1px solid rgb(226 232 240);
  border-radius: 0.75rem;
  background: rgb(248 250 252);
  transition: border-color 0.16s ease, box-shadow 0.16s ease, transform 0.16s ease;
}

.review-image-tile:hover {
  border-color: rgb(147 197 253);
  box-shadow: 0 6px 16px rgb(15 23 42 / 0.1);
  transform: translateY(-1px);
}

.review-image-tile img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.flyer-card-media {
  overflow: hidden;
}

.flyer-card-empty {
  padding: 1rem;
}

.flyer-card-empty-post {
  min-height: 21rem;
}

.flyer-card-empty-masonry {
  min-height: 10rem;
}

.flyer-menu-item {
  display: flex;
  align-items: center;
  gap: 0.55rem;
  min-height: 2.25rem;
  padding: 0 0.8rem;
  text-align: left;
  font-size: 0.82rem;
  font-weight: 500;
  color: rgb(51 65 85);
}

.flyer-menu-item:hover {
  background: rgb(248 250 252);
  color: rgb(15 23 42);
}

.line-clamp-2,
.line-clamp-5 {
  display: -webkit-box;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.line-clamp-2 {
  -webkit-line-clamp: 2;
}

.line-clamp-5 {
  -webkit-line-clamp: 5;
}

@media (max-width: 640px) {
  .flyer-card-empty-post {
    min-height: 18rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .review-rating-emoji {
    animation: none;
  }
}
</style>
